import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  RESOURCE_TYPES,
  SUBMENU_TYPES,
  WRITE_ACCESS_AUTH_ROLES
} from 'utils/appConstants';

import Text from 'components/Text';
import HasRole from 'components/HasRole';
import { ReactComponent as TopoNodeIcon } from 'assets/toponode-icon.svg';
import { ReactComponent as IndeNodeIcon } from 'assets/indenode-icon.svg';
import { ReactComponent as AggregatorIcon } from 'assets/aggregator-icon.svg';
import { ReactComponent as DeviceIcon } from 'assets/device-icon.svg';
import { ReactComponent as ControlIcon } from 'assets/control-icon.svg';
import { ReactComponent as CurveIcon } from 'assets/curve-icon.svg';

const SubmenuContainer = styled.div`
  background-color: #ffffff;
  flex: 0 1 100%;
  max-height: 45px;
  padding: 0 1.2em;
  z-index: 2;
`;

const SubmenuText = styled(Text)``;

const DropdownOptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.12);
  min-width: 158px;
  position: absolute;
  z-index: 3;
`;

const SubmenuLink = styled.button`
  background-color: ${({ dropdownIsShowing }) =>
    dropdownIsShowing ? 'rgba(7, 121, 175, 0.2)' : 'transparent'};
  box-sizing: border-box;
  cursor: pointer;
  font-size: 14px;
  outline: none;
  padding: 1em 0.5em;
  padding-top: 1.2em;
  text-align: left;

  &:hover {
    background-color: ${({ dropdownIsShowing }) =>
      dropdownIsShowing ? 'rgba(7, 121, 175, 0.2)' : 'rgba(7, 121, 175, 0.05)'};
  }

  ${SubmenuText} {
    font-size: 14px;
  }
`;

const DropdownOption = styled(SubmenuLink)`
  background-color: #ffffff;
  display: flex;
  justify-content: flex-start;
  align-items: baseline;
  padding: 0.5em;

  &:first-of-type {
    padding-top: 0.75em;
  }

  &:last-of-type {
    padding-bottom: 0.75em;
  }

  ${SubmenuText} {
    font-size: 16px;
  }
`;

const IconContainer = styled.div`
  min-width: 1.75em;
`;

const TopologyResourcesDropdown = ({
  dropdownIsShowing,
  selectOption,
  changeDropdownVisibility
}) => (
  <DropdownOptionContainer dropdownIsShowing={dropdownIsShowing}>
    <DropdownOption
      onClick={e => {
        selectOption(e, RESOURCE_TYPES.TOPONODE);
        changeDropdownVisibility();
      }}
    >
      <IconContainer>
        <TopoNodeIcon />
      </IconContainer>
      <SubmenuText>Topology Node</SubmenuText>
    </DropdownOption>
    <DropdownOption
      onClick={e => {
        selectOption(e, RESOURCE_TYPES.INDENODE);
        changeDropdownVisibility();
      }}
    >
      <IconContainer>
        <IndeNodeIcon />
      </IconContainer>
      <SubmenuText>Independent Node</SubmenuText>
    </DropdownOption>
    <DropdownOption
      onClick={e => {
        selectOption(e, RESOURCE_TYPES.AGGREGATOR);
        changeDropdownVisibility();
      }}
    >
      <IconContainer>
        <AggregatorIcon />
      </IconContainer>
      <SubmenuText>Aggregator</SubmenuText>
    </DropdownOption>
    <DropdownOption
      onClick={e => {
        selectOption(e, RESOURCE_TYPES.DEVICE);
        changeDropdownVisibility();
      }}
    >
      <IconContainer>
        <DeviceIcon />
      </IconContainer>
      <SubmenuText>Device</SubmenuText>
    </DropdownOption>
    <DropdownOption
      onClick={e => {
        selectOption(e, RESOURCE_TYPES.CONTROL);
        changeDropdownVisibility();
      }}
    >
      <IconContainer>
        <ControlIcon />
      </IconContainer>
      <SubmenuText>Control</SubmenuText>
    </DropdownOption>
    <DropdownOption
      onClick={e => {
        selectOption(e, RESOURCE_TYPES.CURVE);
        changeDropdownVisibility();
      }}
    >
      <IconContainer>
        <CurveIcon />
      </IconContainer>
      <SubmenuText>Curve</SubmenuText>
    </DropdownOption>
  </DropdownOptionContainer>
);

const UsersDropdown = ({ dropdownIsShowing, selectOption }) => (
  <DropdownOptionContainer dropdownIsShowing={dropdownIsShowing}>
    <DropdownOption onClick={e => selectOption(e, RESOURCE_TYPES.USER)}>
      <SubmenuText margin="0 1em">Authorized User</SubmenuText>
    </DropdownOption>
  </DropdownOptionContainer>
);

const Submenu = ({
  toggleDropdownVisibility,
  submenuType,
  selectOption,
  dropdownIsShowing
}) => {
  const changeDropdownVisibility = () => {
    toggleDropdownVisibility(!dropdownIsShowing);
  };

  return (
    <SubmenuContainer>
      <SubmenuLink
        dropdownIsShowing={dropdownIsShowing}
        onClick={changeDropdownVisibility}
      >
        <SubmenuText>New</SubmenuText>
      </SubmenuLink>
      {dropdownIsShowing ? (
        submenuType === SUBMENU_TYPES.TOPOLOGY ? (
          <TopologyResourcesDropdown
            changeDropdownVisibility={changeDropdownVisibility}
            selectOption={selectOption}
          />
        ) : submenuType === SUBMENU_TYPES.USERS ? (
          <UsersDropdown
            dropdownIsShowing={dropdownIsShowing}
            selectOption={selectOption}
          />
        ) : null
      ) : null}
    </SubmenuContainer>
  );
};

const rolesAllowed = allowedRoles =>
  HasRole(Submenu, allowedRoles || [...WRITE_ACCESS_AUTH_ROLES]);

export default rolesAllowed;
